import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../../supabaseClient";
import { 
  Search, CheckCircle2, XCircle, FileText, RefreshCw, 
  AlertTriangle, Edit2, X, ZoomIn, ZoomOut, Download
} from "lucide-react";
import "./PaymentAuditTable.css";

export default function PaymentAuditTable({ eventId: propEventId }) {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(propEventId || "all");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [processing, setProcessing] = useState({});

  // Lightbox Modal State
  const [activeProof, setActiveProof] = useState(null); // { url, row }
  const [zoomScale, setZoomScale] = useState(1);

  // 1. Fetch available events for the selector
  useEffect(() => {
    async function loadEvents() {
      const { data, error } = await supabase
        .from("events")
        .select("id, name")
        .order("created_at", { ascending: false });
      if (!error && data) setEvents(data);
    }
    loadEvents();
  }, []);

  // Update internal selected event if prop changes
  useEffect(() => {
    if (propEventId) setSelectedEventId(propEventId);
  }, [propEventId]);

  // 2. Fetch payments filtered by event
  const fetchPayments = async () => {
    setLoading(true);
    let builder = supabase
      .from("vendor_payment_audit")
      .select("*")
      .order("declared_at", { ascending: false, nullsFirst: false });

    if (selectedEventId && selectedEventId !== "all") {
      builder = builder.eq("event_id", selectedEventId);
    }

    const { data, error } = await builder;
    if (error) {
      console.error("[PaymentAudit] Fetch error:", error);
    } else {
      setPayments(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPayments();
  }, [selectedEventId]);

  // Handle keyboard escape for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setActiveProof(null);
        setZoomScale(1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // 3. Confirm Payment
  const handleConfirm = async (row) => {
    const stageLabel = row.payment_type.toUpperCase();
    if (!window.confirm(`Confirm ${stageLabel} payment for ${row.brand_name}?`)) return;

    setProcessing(p => ({ ...p, [row.record_id]: true }));

    const updatePayload = row.payment_type === "balance"
      ? { balance_confirmed: true, balance_submitted: true }
      : { deposit_confirmed: true, deposit_submitted: true };

    const { error } = await supabase
      .from("vendor_applications")
      .update(updatePayload)
      .eq("id", row.application_id);

    if (error) {
      alert("Failed to confirm: " + error.message);
    } else {
      const endpoint = row.payment_type === "balance"
        ? "/api/send-full-payment-email"
        : "/api/send-deposit-confirmation";

      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ applicationId: row.application_id }),
      }).catch(err => console.error("Email notification failed:", err));

      if (activeProof?.row?.record_id === row.record_id) {
        setActiveProof(null);
      }
      fetchPayments();
    }
    setProcessing(p => ({ ...p, [row.record_id]: false }));
  };

  // 4. Reject Proof
  const handleRejectProof = async (row) => {
    const reason = window.prompt("Reason for rejecting proof (slip will be cleared so vendor can re-upload):");
    if (!reason) return;

    setProcessing(p => ({ ...p, [row.record_id]: true }));

    const updatePayload = {};
    if (row.payment_type === "deposit") {
      updatePayload.deposit_submitted = false;
      updatePayload.deposit_proof_url = null;
    } else if (row.payment_type === "topup") {
      updatePayload.deposit_topup_proof_url = null;
    } else if (row.payment_type === "balance") {
      updatePayload.balance_submitted = false;
      updatePayload.balance_proof_url = null;
    }

    const { error } = await supabase
      .from("vendor_applications")
      .update(updatePayload)
      .eq("id", row.application_id);

    if (error) {
      alert("Failed to reject proof: " + error.message);
    } else {
      if (activeProof?.row?.record_id === row.record_id) {
        setActiveProof(null);
      }
      fetchPayments();
    }
    setProcessing(p => ({ ...p, [row.record_id]: false }));
  };

  // 5. Correct Declared Amount
  const handleEditDeclaredAmount = async (row) => {
    const currentVal = row.declared_amount || "";
    const rawVal = window.prompt(`Correct declared amount for ${row.brand_name} (${row.payment_type}):`, currentVal);
    if (rawVal === null) return;

    const newAmount = parseFloat(rawVal);
    if (isNaN(newAmount) || newAmount < 0) {
      alert("Invalid amount entered.");
      return;
    }

    const field = row.payment_type === "balance"
      ? "balance_declared_amount"
      : row.payment_type === "topup"
      ? "deposit_topup_declared_amount"
      : "deposit_declared_amount";

    const { error } = await supabase
      .from("vendor_applications")
      .update({ [field]: newAmount })
      .eq("id", row.application_id);

    if (error) {
      alert("Failed to update amount: " + error.message);
    } else {
      fetchPayments();
    }
  };

const handleOpenLightbox = async (row) => {
    if (!row.proof_url) {
      alert("No proof document path found for this record.");
      return;
    }
    setZoomScale(1);

    const bucketName = "Vendor_onboarding_forms";
    let raw = row.proof_url.trim().split("?")[0].split("#")[0];

    
    let cleanPath = raw;

    if (cleanPath.includes("/storage/v1/object/")) {
      const splitAfterObject = cleanPath.split("/storage/v1/object/")[1]; // "public/Vendor_onboarding_forms/payments/..."
      const segments = splitAfterObject.split("/");
     
      cleanPath = segments.slice(2).join("/");
    }

    if (cleanPath.startsWith(`${bucketName}/`)) {
      cleanPath = cleanPath.replace(`${bucketName}/`, "");
    }
    cleanPath = decodeURIComponent(cleanPath.replace(/^\/+/, ""));

    console.log("[Proof Debug] Fetching signed URL for path:", cleanPath);

    // 2. Request a signed URL from Supabase Storage (valid for 1 hour)
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(cleanPath, 3600);

    if (error || !data?.signedUrl) {
      console.error("[Storage createSignedUrl Error]:", error);
      alert(`Could not sign URL: ${error?.message || "Object not found"}\nAttempted path: ${cleanPath}`);
      return;
    }

    console.log("[Proof Debug] Signed URL successfully generated:", data.signedUrl);

    // 3. Set the state ONLY with the signed URL
    setActiveProof({ url: data.signedUrl, row });
  };

  // 7. Memoized Filter with Amount Search
  const filtered = useMemo(() => {
    const cleanQuery = query.toLowerCase().trim();
    const numericQuery = cleanQuery.replace(/[^0-9.]/g, "");

    return payments.filter(p => {
      if (stageFilter !== "all" && p.payment_type !== stageFilter) return false;
      if (statusFilter === "pending" && p.is_confirmed) return false;
      if (statusFilter === "confirmed" && !p.is_confirmed) return false;

      if (cleanQuery) {
        const textFields = `${p.brand_name || ""} ${p.vendor_email || ""} ${p.whatsapp_number || ""}`.toLowerCase();
        const matchesText = textFields.includes(cleanQuery);

        let matchesAmount = false;
        if (numericQuery) {
          const declared = String(p.declared_amount || "");
          const target = String(p.target_amount || "");
          const total = String(p.invoice_total || "");
          matchesAmount = declared.includes(numericQuery) || target.includes(numericQuery) || total.includes(numericQuery);
        }

        if (!matchesText && !matchesAmount) return false;
      }
      return true;
    });
  }, [payments, stageFilter, statusFilter, query]);

  const isPdf = activeProof?.url?.toLowerCase().includes(".pdf");

  return (
    <div className="pat-container">
      {/* Header */}
      <div className="pat-header">
        <div>
          <h2>Payment & Proof Ledger</h2>
          <p className="pat-sub">{filtered.length} entries matching criteria</p>
        </div>
        <button className="pat-refresh-btn" onClick={fetchPayments} disabled={loading}>
          <RefreshCw size={14} className={loading ? "pat-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Controls & Scoping */}
      <div className="pat-controls">
        <div className="pat-search">
          <Search size={16} />
          <input
            type="search"
            placeholder="Search brand, email, phone, or amount (e.g. 15000)…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        <div className="pat-filters">
          {/* Event Scope Selector */}
          <select 
            value={selectedEventId} 
            onChange={e => setSelectedEventId(e.target.value)}
            className="pat-select-event"
          >
            <option value="all">All Events</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>

          <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
            <option value="all">All Stages</option>
            <option value="deposit">Deposit Only</option>
            <option value="topup">Deposit Top-Up</option>
            <option value="balance">Balance Only</option>
          </select>

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="pending">Awaiting Confirmation</option>
            <option value="confirmed">Confirmed</option>
            <option value="all">All Statuses</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="pat-table-wrapper">
        <table className="pat-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Event</th>
              <th>Stage</th>
              <th>Invoice / Target</th>
              <th>Declared Amount</th>
              <th>Submission Date</th>
              <th>Proof Slip</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="pat-empty">Loading records…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="9" className="pat-empty">No matching records found.</td></tr>
            ) : (
              filtered.map(row => {
                const isWorking = processing[row.record_id];
                const amountMismatch = row.target_amount && row.declared_amount && Number(row.target_amount) !== Number(row.declared_amount);

                return (
                  <tr key={row.record_id} className={amountMismatch && !row.is_confirmed ? "pat-row-warn" : ""}>
                    <td>
                      <strong>{row.brand_name || "—"}</strong>
                      <div className="pat-meta">{row.vendor_email}</div>
                    </td>
                    <td>
                      <span className="pat-event-tag">{row.event_name || "—"}</span>
                    </td>
                    <td>
                      <span className={`pat-pill pat-pill--${row.payment_type}`}>
                        {row.payment_type.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <div>Total: KES {Number(row.invoice_total || 0).toLocaleString()}</div>
                      <div className="pat-meta">Target: KES {Number(row.target_amount || 0).toLocaleString()}</div>
                    </td>
                    <td>
                      <div className="pat-amount-cell">
                        <strong>KES {Number(row.declared_amount || 0).toLocaleString()}</strong>
                        <button 
                          className="pat-btn-ghost" 
                          title="Correct declared amount"
                          onClick={() => handleEditDeclaredAmount(row)}
                        >
                          <Edit2 size={12} />
                        </button>
                      </div>
                      {amountMismatch && (
                        <div className="pat-warn-text">
                          <AlertTriangle size={11} /> Mismatch with target
                        </div>
                      )}
                    </td>
                    <td>
                      {row.declared_at 
                        ? new Date(row.declared_at).toLocaleString("en-KE", { timeZone: "Africa/Nairobi", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) 
                        : "—"}
                    </td>
                    <td>
                      {row.proof_url ? (
                        <button 
                          className="pat-proof-link" 
                          onClick={() => handleOpenLightbox(row)}
                          title="Inspect receipt in lightbox"
                        >
                          <FileText size={14} /> View Slip
                        </button>
                      ) : (
                        <span className="pat-meta">No slip</span>
                      )}
                    </td>
                    <td>
                      {row.is_confirmed ? (
                        <span className="pat-status-tag pat-status-tag--confirmed">
                          <CheckCircle2 size={12} /> Confirmed
                        </span>
                      ) : (
                        <span className="pat-status-tag pat-status-tag--pending">
                          Pending
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="pat-actions">
                        {!row.is_confirmed && (
                          <button
                            className="pat-btn-action pat-btn-confirm"
                            disabled={isWorking}
                            onClick={() => handleConfirm(row)}
                          >
                            <CheckCircle2 size={13} /> Confirm
                          </button>
                        )}
                        {row.proof_url && !row.is_confirmed && (
                          <button
                            className="pat-btn-action pat-btn-reject"
                            disabled={isWorking}
                            onClick={() => handleRejectProof(row)}
                            title="Reject slip and allow re-upload"
                          >
                            <XCircle size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Lightweight Proof Lightbox Modal */}
      {activeProof && (
        <div className="pat-lightbox-overlay" onClick={() => setActiveProof(null)}>
          <div className="pat-lightbox-modal" onClick={e => e.stopPropagation()}>
            <div className="pat-lightbox-header">
              <div>
                <h4>{activeProof.row.brand_name} — {activeProof.row.payment_type.toUpperCase()} PROOF</h4>
                <p>Declared: KES {Number(activeProof.row.declared_amount || 0).toLocaleString()} (Target: KES {Number(activeProof.row.target_amount || 0).toLocaleString()})</p>
              </div>

              <div className="pat-lightbox-tools">
                {!isPdf && (
                  <>
                    <button onClick={() => setZoomScale(s => Math.max(0.5, s - 0.25))} title="Zoom out">
                      <ZoomOut size={16} />
                    </button>
                    <span>{Math.round(zoomScale * 100)}%</span>
                    <button onClick={() => setZoomScale(s => Math.min(3, s + 0.25))} title="Zoom in">
                      <ZoomIn size={16} />
                    </button>
                  </>
                )}
                <a href={activeProof.url} target="_blank" rel="noreferrer" download title="Open/Download original">
                  <Download size={16} />
                </a>
                <button className="pat-lightbox-close" onClick={() => setActiveProof(null)}>
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="pat-lightbox-viewport">
              {isPdf ? (
                <iframe src={activeProof.url} title="PDF Slip Viewer" className="pat-lightbox-iframe" />
              ) : (
                <div className="pat-lightbox-img-wrapper">
                  <img 
                    src={activeProof.url} 
                    alt="Payment Slip Proof" 
                    style={{ transform: `scale(${zoomScale})` }}
                    className="pat-lightbox-image" 
                  />
                </div>
              )}
            </div>

            <div className="pat-lightbox-footer">
              <span className="pat-meta">Tip: Press ESC to close</span>
              <div className="pat-actions">
                {!activeProof.row.is_confirmed && (
                  <>
                    <button 
                      className="pat-btn-action pat-btn-reject"
                      onClick={() => handleRejectProof(activeProof.row)}
                    >
                      <XCircle size={14} /> Reject Proof
                    </button>
                    <button 
                      className="pat-btn-action pat-btn-confirm"
                      onClick={() => handleConfirm(activeProof.row)}
                    >
                      <CheckCircle2 size={14} /> Confirm KES {Number(activeProof.row.declared_amount || 0).toLocaleString()}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}